import { Menu } from '@headlessui/react';
import { DuplicateIcon, LogoutIcon } from '@heroicons/react/outline';
import React, { ReactNode, useEffect, useState } from 'react';
import useDriftStore from '../stores/DriftStore/useDriftStore';
import UI_UTILS from '../utils/uiUtils';
import Button from './Button';
import Chevron from './Icons/Chevron';
import useAccountData from 'src/hooks/useAccountData';
// import { SubAccountsList } from './SubAccountSwitcher';
import CurrentWalletProviderIcon from './Icons/CurrentWalletProviderIcon';
import { Settings } from 'react-feather';
import { useRouter } from 'next/router';
import useCurrentWalletAdapter from '../hooks/useCurrentWalletAdapter';
import useHasConnectedWalletBefore from '../hooks/useHasConnectedWalletBefore';
import useSafePush from 'src/hooks/useSafePush';
import useWalletsToDisplay from 'src/hooks/useWalletsToDisplay';
import useHandleSelectWalletProvider from 'src/hooks/useHandleSelectWalletProvider';
import { ChevronDown } from '@drift-labs/icons';
import usePrefferredWalletSetting from 'src/hooks/usePrefferredWalletSetting';
import {
	MAGIC_AUTH_WALLET_ADAPTER_NAME,
	SNAP_WALLET_ADAPTER_NAME,
} from 'src/constants/constants';
import Text from './Text/Text';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import { SnapWalletAdapter } from '@drift-labs/snap-wallet-adapter';
import useHasAcknowledgedTerms from 'src/hooks/useHasAcknowledgedTerms';
import useShowGridCustomiser from 'src/hooks/useShowGridCustomiser';

const ConnectWalletDropdownButton = ({
	preferredWalletName,
	preferredWalletIcon,
}: {
	preferredWalletName: string;
	preferredWalletIcon: string;
}) => {
	const safePush = useSafePush();
	const { handleSelectProvider } = useHandleSelectWalletProvider();
	const [hasPreviouslyConnectedWallet] = useHasConnectedWalletBefore();

	const handleSelectWallet = (name: string) => {
		if (name === MAGIC_AUTH_WALLET_ADAPTER_NAME) {
			// Route to email signup page
			safePush(
				hasPreviouslyConnectedWallet
					? '/onboarding/login-with-email'
					: '/onboarding/signup-with-email'
			);
		} else {
			handleSelectProvider(name);
		}
	};

	const handleOpenPopup = () => {
		safePush('/onboarding/connect');
	};

	return (
		<div
			className="flex flex-row items-center bg-brand-gradient h-[31px] rounded-[2px]"
			id="connect-wallet-dropdown-button"
		>
			<button
				className={`text-xs md:text-sm whitespace-nowrap flex flex-row items-center`}
				onClick={() => handleSelectWallet(preferredWalletName)}
			>
				<img
					src={preferredWalletIcon}
					className="w-5 h-5 ml-3 mr-2 border rounded-full border-connect-button-icon-border bg-connect-button-icon-background"
				/>
				<div className="text-left">
					<div className="text-left">
						<Text.BODY2 className="leading-3 tracking-normal text-text-gradient-button">
							Connect{' '}
						</Text.BODY2>
					</div>
					<div className="mt-[-8px] text-left">
						<Text.MICRO1 className="leading-3 tracking-normal text-text-gradient-button">
							{preferredWalletName === SNAP_WALLET_ADAPTER_NAME
								? 'MetaMask'
								: preferredWalletName === MAGIC_AUTH_WALLET_ADAPTER_NAME
								? 'Email'
								: preferredWalletName}
						</Text.MICRO1>
					</div>
				</div>
			</button>
			<button
				className={`text-xs md:text-sm whitespace-nowrap border-l border-container-border p-2 ml-4 flex flex-row items-center`}
				onClick={handleOpenPopup}
			>
				<div className="w-4 h-4 transition-all">
					<ChevronDown className="w-4 h-4" />
				</div>
			</button>
			{/* {showPopup && (
				<TargettedPopup
					side={'bottomRight'}
					onClose={handleClosePopup}
					targetElementId="connect-wallet-dropdown-button"
				>
					<div>
						{walletsToShow.map((walletProvider) => (
							<button
								onClick={() => handleSelectWallet(walletProvider.adapter.name)}
								key={walletProvider.adapter.name}
								className={buttonClassName}
							>
								<div className="flex flex-row items-center">
									<div className="w-4 h-4 mr-4">
										<img
											src={walletProvider.adapter.icon}
											className="w-full h-full"
										/>
									</div>
									<Text.BODY2>{walletProvider.adapter.name}</Text.BODY2>
								</div>
							</button>
						))}
						{Env.enableMmSnap && metamaskButton}
						{emailButton}
						{showHiddenWallets ? (
							hiddenWallets.map((walletProvider) => (
								<button
									onClick={() =>
										handleSelectWallet(walletProvider.adapter.name)
									}
									key={walletProvider.adapter.name}
									className={buttonClassName}
								>
									<div className="flex flex-row items-center">
										<div className="w-4 h-4 mr-4">
											<img
												src={walletProvider.adapter.icon}
												className="w-full h-full"
											/>
										</div>
										<Text.BODY2>{walletProvider.adapter.name}</Text.BODY2>
									</div>
								</button>
							))
						) : (
							<button className={buttonClassName} onClick={handleShowMore}>
								Other
							</button>
						)}
					</div>
				</TargettedPopup>
			)} */}
		</div>
	);
};

const ConnectWalletButton = (props: {
	className?: string;
	children?: ReactNode;
	fullWidth?: boolean;
}) => {
	const [_, showGridCustomiser] = useShowGridCustomiser();
	const isMobile = useIsMobileScreenSize();
	const showWalletConnectionModal = useDriftStore(
		(s) => s.modals.showWalletConnectionModal
	);
	const setState = useDriftStore((s) => s.set);
	const isMagicAuth = useDriftStore((s) => s.wallet.isMagicAuth);
	const isMetamask = useDriftStore((s) => s.wallet.isMetamask);
	const isAccountsSidebarOpen = useDriftStore(
		(s) => s.modals.showAccountsSidebarModal
	);
	const wallet = useCurrentWalletAdapter();
	const [isCopied, setIsCopied] = useState(false);
	const currentAccount = useAccountData();
	const router = useRouter();
	const safePush = useSafePush();
	const [hasPreviouslyConnectedWallet] = useHasConnectedWalletBefore();
	const [hasAcknowledgedTerms] = useHasAcknowledgedTerms();
	const { walletsToShow, hiddenWallets } = useWalletsToDisplay(1);
	const [preferredWallet] = usePrefferredWalletSetting();
	const connectButtonPath = hasPreviouslyConnectedWallet
		? `/onboarding/connect`
		: `/onboarding/signup`;

	const showTerms =
		!hasAcknowledgedTerms.hasPreviouslyAcknowledgedTerms ||
		hasAcknowledgedTerms.shouldShowTermsReminder;

	const solBalance = useDriftStore((s) => s.wallet.currentSolBalance);

	const preferredWalletProvider = [...walletsToShow, ...hiddenWallets].find(
		(wallet) => wallet?.provider.adapter.name === preferredWallet
	);
	const preferredMagicAuth = preferredWallet === MAGIC_AUTH_WALLET_ADAPTER_NAME;
	const preferredMetamask = preferredWallet === SNAP_WALLET_ADAPTER_NAME;

	const preferredWalletName = preferredMagicAuth
		? MAGIC_AUTH_WALLET_ADAPTER_NAME
		: preferredMetamask
		? SNAP_WALLET_ADAPTER_NAME
		: preferredWalletProvider?.provider?.adapter?.name;
	const preferredWalletIcon = preferredMagicAuth
		? `/assets/icons/magic-auth-logo.svg`
		: preferredMetamask
		? SnapWalletAdapter.icon
		: preferredWalletProvider?.provider?.adapter?.icon;

	const walletOptions = [
		{
			name: `${solBalance?.toFixed(2) ?? 0} SOL`,
			icon: <CurrentWalletProviderIcon />,
		},
		{ name: 'Copy Wallet Address', icon: <DuplicateIcon /> },
		{ name: isMagicAuth ? 'Log Out' : 'Disconnect', icon: <LogoutIcon /> },
	];

	if (isMagicAuth) {
		walletOptions.splice(1, 0, {
			name: 'Manage Magic Wallet',
			icon: <Settings size={14} className="relative top-[1px] left-[1px]" />,
		});
	}

	if (isMetamask) {
		walletOptions.splice(1, 0, {
			name: 'Manage Metamask Wallet',
			icon: <Settings size={14} className="relative top-[1px] left-[1px]" />,
		});
	}

	useEffect(() => {
		if (isCopied) {
			const timer = setTimeout(() => {
				setIsCopied(false);
			}, 2000);
			return () => clearTimeout(timer);
		}
	}, [isCopied]);

	const handleGoToConnectRoute = () => {
		setState((s) => {
			s.wallet.lastRouteBeforeConnect = router.asPath;
		});
		safePush(connectButtonPath);
	};

	useEffect(() => {
		if (wallet.connected && showWalletConnectionModal) {
			setState((s) => {
				s.modals.showWalletConnectionModal = false;
			});
		}
	}, [wallet.connected, showWalletConnectionModal]);

	return (
		<>
			<div
				id="wallet_button"
				className={`items-center ${props.className ?? ''}`}
			>
				{wallet.connected && wallet?.publicKey ? (
					<Menu>
						{({ open }) => (
							<div>
								<Menu.Button
									className="bg-transparent"
									onClick={() => {
										showGridCustomiser(false);
										setState((s) => {
											if (isAccountsSidebarOpen) {
												s.modals.showAccountsSidebarModal = false;
											} else {
												s.modals.showAccountsSidebarModal = true;
											}
										});
									}}
								>
									<Button.Wallet
										size="MEDIUM"
										className="min-w-[135px]"
										isDelegate={currentAccount?.isDelegatedTo}
									>
										<div>
											{isCopied ? (
												<div className="font-bold">Copied!</div>
											) : (
												<div className="flex flex-col items-start justify-center ml-2">
													<div className="flex flex-col items-start">
														<div
															className={`mt-0.5 text-xs md:text-sm font-bold text-text-default whitespace-nowrap`}
															style={{ lineHeight: '14px', fontSize: '14px' }}
														>
															{currentAccount?.isDelegatedTo
																? UI_UTILS.getDelegateAccountLabel(
																		currentAccount
																  )
																: `${currentAccount?.name.slice(0, 13) || ''}${
																		currentAccount?.name?.length > 13
																			? ' ...'
																			: ''
																  }`}
														</div>
														<div
															className={`whitespace-nowrap text-2xs md:text-xs text-text-secondary`}
															style={{ lineHeight: '14px', fontSize: '10px' }}
														>
															{UI_UTILS.abbreviateAddress(wallet?.publicKey)}
														</div>
													</div>
												</div>
											)}
										</div>
										{!isMobile && (
											<Chevron
												direction={open ? 'up' : 'down'}
												className="h-[12px] w-[12px] ml-2"
											/>
										)}
									</Button.Wallet>
								</Menu.Button>
								{/* <Menu.Items
									className={`${ZINDEX.xTop} bg-container-bg text-text-default mt-1 absolute right-0 divide-y shadow-lg outline-none w-[250px] p-0`}
								>
									<div className="w-full h-full border rounded-sm border-container-border">
										<SubAccountsList />
										{walletOptions.map(({ name, icon }, idx) => (
											<Menu.Item key={name}>
												<button
													className={`flex flex-row items-center w-full p-2 font-normal rounded-none hover:bg-container-bg-hover focus:outline-none ${
														idx === 1 && 'rounded-t-md'
													} ${
														idx === walletOptions.length - 1 && 'rounded-b-md'
													} ${idx === 0 ? 'hover:cursor-pointer' : ''}`}
													onClick={(e) => {
														e.preventDefault();
														e.stopPropagation();
														handleWalletMenu(name);
													}}
												>
													<div className="w-4 h-4 mr-2">{icon}</div>
													{name}
												</button>
											</Menu.Item>
										))}
									</div>
								</Menu.Items> */}
							</div>
						)}
					</Menu>
				) : hasPreviouslyConnectedWallet &&
				  !showTerms &&
				  (preferredWalletProvider ||
						preferredMagicAuth ||
						preferredMetamask) &&
				  !isMobile ? (
					<ConnectWalletDropdownButton
						preferredWalletIcon={preferredWalletIcon}
						preferredWalletName={preferredWalletName}
					/>
				) : (
					<Button.Secondary
						highlight
						size="MEDIUM"
						className={`text-xs md:text-sm whitespace-nowrap ${
							props.fullWidth ? 'w-full' : ''
						}`}
						onClick={handleGoToConnectRoute}
					>
						{props.children ? (
							props.children
						) : (
							<span className="text-darkBlue-110">Connect</span>
						)}
					</Button.Secondary>
				)}
			</div>
		</>
	);
};

export default ConnectWalletButton;
